import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
} from '@mui/material';
import BasicInfoForm from '../components/assessment/BasicInfoForm';
import BasicAbilitiesTest from '../components/assessment/BasicAbilitiesTest';
import RIASECTest from '../components/assessment/RIASECTest';
import WorkValuesTest from '../components/assessment/WorkValuesTest';
import PortableSkillsTest from '../components/assessment/PortableSkillsTest';
import { AnswerData } from '../types';

interface BasicInfo {
  name: string;
  age: number;
  gender: string;
  sport: string;
  yearsOfExperience: number;
  competitionLevel: string;
  email: string;
}

const steps = [
  '基本情報',
  '社会人基礎能力',
  '職業興味検査',
  '仕事価値観',
  'ポータブルスキル',
];

const AssessmentFlow: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [basicInfo, setBasicInfo] = useState<BasicInfo | null>(null);
  const [answers, setAnswers] = useState<AnswerData[]>([]);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    window.scrollTo(0, 0);
  };

  const handleBasicInfoSubmit = (info: BasicInfo) => {
    setBasicInfo(info);
    handleNext();
  };

  const handleTestSubmit = (testAnswers: AnswerData[]) => {
    setAnswers((prev) => [...prev, ...testAnswers]);
    if (activeStep === steps.length - 1) {
      // 最後のステップなので結果画面へ
      navigate('/results', {
        state: {
          basicInfo,
          answers: [...answers, ...testAnswers],
        },
      });
    } else {
      handleNext();
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <BasicInfoForm onSubmit={handleBasicInfoSubmit} />;
      case 1:
        return (
          <BasicAbilitiesTest onSubmit={handleTestSubmit} onBack={handleBack} />
        );
      case 2:
        return <RIASECTest onSubmit={handleTestSubmit} onBack={handleBack} />;
      case 3:
        return (
          <WorkValuesTest onSubmit={handleTestSubmit} onBack={handleBack} />
        );
      case 4:
        return (
          <PortableSkillsTest onSubmit={handleTestSubmit} onBack={handleBack} />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* ヘッダー */}
      <Box
        sx={{
          bgcolor: '#0066cc',
          color: 'white',
          py: 3,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            キャリア診断
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 6 }}>
        {/* ステッパー */}
        <Box sx={{ mb: 6 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* コンテンツ */}
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 2,
            p: 4,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          {renderStepContent(activeStep)}
        </Box>
      </Container>
    </Box>
  );
};

export default AssessmentFlow;
